// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Access-control helpers for the provider dashboard. Pure functions — no React,
// no side effects — so they can be exhaustively unit-tested. UX-gating only;
// the backend enforces real authz on every request.

import type { Role } from '@/types'

// Numeric rank per role. Operators (qh_admin+) can act across orgs; org_admin
// administers their own org; clinic_staff/provider are scoped members.
export const RANK: Record<Role, number> = {
  platform_superadmin: 4,
  qh_admin: 3,
  org_admin: 2,
  clinic_staff: 1,
  provider: 1,
}

/**
 * True iff the role is a QH/Quantino operator (qh_admin or platform_superadmin).
 * Operators get the cross-org switcher in the provider dashboard.
 */
export const isOperator = (role: Role | null | undefined): boolean =>
  !!role && RANK[role] >= RANK.qh_admin

/** True iff the role can administer an org's providers (org_admin or operator). */
export const isOrgAdmin = (role: Role | null | undefined): boolean =>
  !!role && RANK[role] >= RANK.org_admin

/** True iff the role is clinical staff. */
export const isClinicalStaff = (role: Role | null | undefined): boolean =>
  role === 'clinic_staff'
